import { presentPanTiles } from './fractalPixels';
import { isPatternMode, iterateUntilEscape, iterateUntilEscapePatternMode } from './fractalIteration';
import { samplePalette } from './fractalPalette';
import { canCertifyCpuPalette } from './cpuExactPan';
import { fullConjugateRowMap } from './fractalConjugateRows';

const BUDDHABROT_MAX_ITERATIONS = 420;
const ESCAPE_THRESHOLD = 4; // 2^2
const LOG2_INV = 1 / Math.log(2);
const BUDDHABROT_MAX_SAMPLES = 1280000;
const BUDDHABROT_QUANTILE_BINS = 1024;
const BUDDHABROT_TONE_LUT_SIZE = 1024;
const BUDDHABROT_SPECTRAL_SIZE = 512;

let buddhabrotToneLut = null;
let buddhabrotSqrtLut = null;
let buddhabrotSpecRLut = null;
let buddhabrotSpecGLut = null;
let buddhabrotSpecBLut = null;
let buddhabrotBinsScratch = null;
let buddhabrotSmoothScratch = null;
let buddhabrotSmoothBuf = null;

function getBuddhabrotLuts() {
  if (!buddhabrotToneLut) {
    buddhabrotToneLut = new Float32Array(BUDDHABROT_TONE_LUT_SIZE + 1);
    buddhabrotSqrtLut = new Float32Array(BUDDHABROT_TONE_LUT_SIZE + 1);
    for (let i = 0; i <= BUDDHABROT_TONE_LUT_SIZE; i++) {
      const u = i / BUDDHABROT_TONE_LUT_SIZE;
      buddhabrotSqrtLut[i] = Math.pow(u, 0.50) * (u / (u + 0.014)) * 1.02;
      buddhabrotToneLut[i] = Math.pow(u, 0.68) * (u / (u + 0.024));
    }
    buddhabrotSpecRLut = new Float32Array(BUDDHABROT_SPECTRAL_SIZE);
    buddhabrotSpecGLut = new Float32Array(BUDDHABROT_SPECTRAL_SIZE);
    buddhabrotSpecBLut = new Float32Array(BUDDHABROT_SPECTRAL_SIZE);
    for (let m = 0; m < BUDDHABROT_SPECTRAL_SIZE; m++) {
      // Wikipedia 3-tier Nebulabrot spectral response:
      // Tier 1 (fast escape / early steps): deep amethyst / purple veil
      // Tier 2 (medium escape / mid steps): electric sapphire & cobalt caustics
      // Tier 3 (slow escape / deep caustics): radiant ice-cyan & incandescent core
      let wShort = 0;
      let wMid = 0;
      let wLong = 0;
      if (m <= 52) {
        wShort = m < 16 ? Math.max(0.2, (m - 4) / 12) : 1.0;
        if (m >= 28) {
          const t = (m - 28) / 24;
          wMid = t * t * (3 - 2 * t);
          wShort *= 1 - 0.42 * t;
        }
      } else if (m <= 156) {
        const t = (m - 52) / 104;
        wShort = t < 0.32 ? (1 - t / 0.32) * 0.58 : 0;
        wMid = t < 0.62 ? 1.0 : Math.max(0.18, 1 - (t - 0.62) * 1.8);
        wLong = t > 0.18 ? Math.pow((t - 0.18) / 0.82, 1.12) * 1.05 : 0;
      } else {
        const t = Math.min(1, (m - 156) / 180);
        wMid = (1 - t) * 0.22;
        wLong = 1.05 + 0.35 * t;
      }
      buddhabrotSpecRLut[m] = wShort;
      buddhabrotSpecGLut[m] = wMid;
      buddhabrotSpecBLut[m] = wLong;
    }
  }
  return {
    toneLut: buddhabrotToneLut,
    sqrtLut: buddhabrotSqrtLut,
    specRLut: buddhabrotSpecRLut,
    specGLut: buddhabrotSpecGLut,
    specBLut: buddhabrotSpecBLut,
  };
}

function getBuddhabrotScratch(pixelCount) {
  if (!buddhabrotBinsScratch) {
    buddhabrotBinsScratch = new Uint32Array(BUDDHABROT_QUANTILE_BINS);
  }
  if (!buddhabrotSmoothScratch || buddhabrotSmoothScratch.length < pixelCount) {
    buddhabrotSmoothScratch = new Float32Array(pixelCount);
  }
  if (!buddhabrotSmoothBuf || buddhabrotSmoothBuf.length < pixelCount) {
    buddhabrotSmoothBuf = new Float32Array(pixelCount);
  }
  return {
    binsScratch: buddhabrotBinsScratch,
    smoothScratch: buddhabrotSmoothScratch.subarray(0, pixelCount),
    smoothBuf: buddhabrotSmoothBuf.subarray(0, pixelCount),
  };
}

export function isBuddhabrotMode(mode) {
  return mode === 'buddhabrot' || mode === 'nebulabrot';
}

export function buddhabrotColor({ numIterations = 0 } = {}) {
  const v = Math.min(255, numIterations * 7);
  return [255, v, 0, Math.min(255, numIterations * 15)];
}

buddhabrotColor.colorParams = Object.freeze({
  palette: 'buddhabrot',
  mode: 'buddhabrot',
  useMu: false,
});

function isCardioidOrBulbFast(x0, y0) {
  const xm = x0 - 0.25;
  const y20 = y0 * y0;
  const q = xm * xm + y20;
  const xp1 = x0 + 1;
  const xBulb3 = x0 + 1.309;
  return q * (q + xm) <= 0.25 * y20 || xp1 * xp1 + y20 <= 0.0625 || xBulb3 * xBulb3 + y20 <= 0.0032;
}

function quickBuddhabrotEscapeIter(x0, y0, maxIter = BUDDHABROT_MAX_ITERATIONS) {
  if (isCardioidOrBulbFast(x0, y0)) return BUDDHABROT_MAX_ITERATIONS;
  let x = x0;
  let y = y0;
  let x2 = x * x;
  let y2 = y * y;
  let iter = 1;
  let ox = NaN;
  let oy = NaN;
  let checkAt = 8;
  let step = 0;
  while (x2 + y2 < ESCAPE_THRESHOLD && iter < maxIter) {
    const xy = x * y;
    x = x2 - y2 + x0;
    y = xy + xy + y0;
    x2 = x * x;
    y2 = y * y;
    iter++;
    if (x === ox && y === oy) return BUDDHABROT_MAX_ITERATIONS;
    if (++step === checkAt) {
      ox = x;
      oy = y;
      step = 0;
      checkAt <<= 1;
    }
  }
  return iter >= maxIter ? BUDDHABROT_MAX_ITERATIONS : iter;
}

/**
 * Choose the starting-orbit sample grid dimensions for a Buddhabrot render.
 * Supersamples 2x2 at inline resolutions while capping total orbits on large viewports.
 */
export function getBuddhabrotSampleGrid(canvasWidth, canvasHeight) {
  const targetW = Math.max(1, canvasWidth * 2);
  const targetH = Math.max(1, canvasHeight * 2);
  const total = targetW * targetH;
  const scale = total > BUDDHABROT_MAX_SAMPLES
    ? Math.sqrt(BUDDHABROT_MAX_SAMPLES / total)
    : 1;
  return {
    samplesX: Math.max(canvasWidth, Math.round(targetW * scale)),
    samplesY: Math.max(canvasHeight, Math.round(targetH * scale)),
  };
}

/**
 * Accumulate Buddhabrot orbit visit counts across a horizontal slice of the sample grid.
 * Returns a Float32Array of length `canvasWidth * canvasHeight * 4` storing 4 planes:
 * [rHist, gHist, bHist, boundaryHist].
 */
export function accumulateBuddhabrotHistogram({
  canvasWidth,
  canvasHeight,
  xRange,
  yRange,
  samplesX: rawSamplesX,
  samplesY: rawSamplesY,
  startSampleY = 0,
  sampleRegionHeight,
  motionPreview = false,
}) {
  const pixelCount = canvasWidth * canvasHeight;
  const histBuffer = new Float32Array(pixelCount * 4);
  if (!(canvasWidth > 0 && canvasHeight > 0)) return histBuffer;

  const grid = rawSamplesX && rawSamplesY
    ? { samplesX: rawSamplesX, samplesY: rawSamplesY }
    : getBuddhabrotSampleGrid(canvasWidth, canvasHeight);
  const { samplesX, samplesY } = grid;
  const endSampleY = Math.min(
    samplesY,
    startSampleY + (sampleRegionHeight ?? samplesY),
  );
  if (startSampleY >= endSampleY) return histBuffer;

  const rHist = histBuffer.subarray(0, pixelCount);
  const gHist = histBuffer.subarray(pixelCount, pixelCount * 2);
  const bHist = histBuffer.subarray(pixelCount * 2, pixelCount * 3);
  const shipHist = histBuffer.subarray(pixelCount * 3, pixelCount * 4);

  const xMin = xRange[0];
  const xMax = xRange[1];
  const yMin = yRange[0];
  const yMax = yRange[1];
  const xSpan = xMax - xMin;
  const ySpan = yMax - yMin;
  if (!(xSpan > 0 && ySpan > 0)) return histBuffer;

  const scaleX = canvasWidth / xSpan;
  const scaleY = canvasHeight / ySpan;
  const biasX = -xMin * scaleX - 0.5;
  const biasY = -yMin * scaleY - 0.5;
  const xInnerMin = xMin + 0.5 / scaleX;
  const xInnerMax = xMax - 1.5 / scaleX;
  const yInnerMin = yMin + 0.5 / scaleY;
  const yInnerMax = yMax - 1.5 / scaleY;
  const maxCornerX2 = Math.max(xInnerMin * xInnerMin, xInnerMax * xInnerMax);
  const maxCornerY2 = Math.max(yInnerMin * yInnerMin, yInnerMax * yInnerMax);
  const viewInsideDisk = maxCornerX2 + maxCornerY2 <= ESCAPE_THRESHOLD;

  const maxIter = motionPreview ? 256 : BUDDHABROT_MAX_ITERATIONS;
  const rowStride = motionPreview && (endSampleY - startSampleY >= 4) ? 2 : 1;

  const splatBilinear = (hist, fx, fy, w) => {
    const ix = fx | 0;
    const iy = fy | 0;
    const tx = fx - ix;
    const ty = fy - iy;
    const omtX = (1 - tx) * w;
    const txW = tx * w;
    const omtY = 1 - ty;
    const idx = iy * canvasWidth + ix;
    hist[idx] += omtX * omtY;
    hist[idx + 1] += txW * omtY;
    hist[idx + canvasWidth] += omtX * ty;
    hist[idx + canvasWidth + 1] += txW * ty;
  };

  const addBoundarySample = (x0, y0, iter, distSq, weight, mirrorY) => {
    if (!(weight > 0) || x0 <= xInnerMin || x0 >= xInnerMax) return;
    let smoothIter = iter;
    if (distSq > 4.0) {
      const logZn = 0.5 * Math.log(distSq);
      const nu = Math.log(logZn * LOG2_INV) * LOG2_INV;
      if (Number.isFinite(nu)) smoothIter = iter + 1 - nu;
    }
    const rawVal = smoothIter - 20;
    if (rawVal <= 0 || smoothIter >= 140) return;
    const rise = 1 - Math.exp(-rawVal * 0.075);
    const t = smoothIter > 55 ? (140 - smoothIter) / 85 : 1;
    const taper = t * t;
    const shipVal = 20 * rise * rise * taper * weight;
    const fx = x0 * scaleX + biasX;
    if (y0 > yInnerMin && y0 < yInnerMax) {
      splatBilinear(shipHist, fx, y0 * scaleY + biasY, shipVal);
    }
    if (mirrorY) {
      const ny0 = -y0;
      if (ny0 > yInnerMin && ny0 < yInnerMax) {
        splatBilinear(shipHist, fx, ny0 * scaleY + biasY, shipVal);
      }
    }
  };

  const hitX = new Float64Array(BUDDHABROT_MAX_ITERATIONS);
  const hitY = new Float64Array(BUDDHABROT_MAX_ITERATIONS);
  const activeCols = new Int32Array(samplesX);
  const enableSubSampling = !motionPreview && canvasWidth >= 16 && canvasHeight >= 16;
  const { specRLut, specGLut, specBLut } = getBuddhabrotLuts();

  const scatterWeighted = (fx, fy, wR, wG, wB) => {
    const ix = fx | 0;
    const iy = fy | 0;
    const tx = fx - ix;
    const ty = fy - iy;
    const omtX = 1 - tx;
    const omtY = 1 - ty;
    const w00 = omtX * omtY;
    const w10 = tx * omtY;
    const w01 = omtX * ty;
    const w11 = tx * ty;
    const idx = iy * canvasWidth + ix;
    if (wR > 0) {
      rHist[idx] += w00 * wR;
      rHist[idx + 1] += w10 * wR;
      rHist[idx + canvasWidth] += w01 * wR;
      rHist[idx + canvasWidth + 1] += w11 * wR;
    }
    if (wG > 0) {
      gHist[idx] += w00 * wG;
      gHist[idx + 1] += w10 * wG;
      gHist[idx + canvasWidth] += w01 * wG;
      gHist[idx + canvasWidth + 1] += w11 * wG;
    }
    if (wB > 0) {
      bHist[idx] += w00 * wB;
      bHist[idx + 1] += w10 * wB;
      bHist[idx + canvasWidth] += w01 * wB;
      bHist[idx + canvasWidth + 1] += w11 * wB;
    }
  };

  const traceAndScatter = (x0, y0, orbitWeight, shipWeight, mirrorY) => {
    if (isCardioidOrBulbFast(x0, y0)) return;
    let x = x0;
    let y = y0;
    let x2 = x * x;
    let y2 = y * y;
    let iter = 1;
    let ox = NaN;
    let oy = NaN;
    let checkAt = 8;
    let step = 0;
    while (x2 + y2 < ESCAPE_THRESHOLD && iter < maxIter) {
      const xy = x * y;
      x = x2 - y2 + x0;
      y = xy + xy + y0;
      x2 = x * x;
      y2 = y * y;
      hitX[iter] = x;
      hitY[iter] = y;
      iter++;
      if (x === ox && y === oy) {
        iter = maxIter;
        break;
      }
      if (++step === checkAt) {
        ox = x;
        oy = y;
        step = 0;
        checkAt <<= 1;
      }
    }
    if (iter >= maxIter || iter < 15) return;
    if (shipWeight > 0 && iter >= 20) {
      addBoundarySample(x0, y0, iter, x2 + y2, shipWeight, mirrorY);
    }
    if (!(orbitWeight > 0) || iter < 16) return;
    const kLimit = viewInsideDisk ? iter - 1 : iter;
    let smoothIter = iter;
    const distSq = x2 + y2;
    if (distSq > 4.0) {
      const logZn = 0.5 * Math.log(distSq);
      const nu = Math.log(logZn * LOG2_INV) * LOG2_INV;
      if (Number.isFinite(nu)) smoothIter = iter + 1 - nu;
    }
    const baseM = Math.sqrt(Math.max(0, smoothIter - 14)) * 5.2;
    for (let k = 1; k < kLimit; k++) {
      const xk = hitX[k];
      if (xk >= xInnerMax || xk <= xInnerMin) continue;
      const yk = hitY[k];
      const fx = xk * scaleX + biasX;
      const sf = (k === 1 ? 0.35 : (k === 2 ? 0.72 : 1.0)) * orbitWeight;
      const mIdx = Math.min(BUDDHABROT_SPECTRAL_SIZE - 1, (baseM + k * 3.1) | 0);
      const wR = specRLut[mIdx] * sf;
      const wG = specGLut[mIdx] * sf;
      const wB = specBLut[mIdx] * sf;
      if (yk > yInnerMin && yk < yInnerMax) {
        scatterWeighted(fx, yk * scaleY + biasY, wR, wG, wB);
      }
      if (mirrorY) {
        const nyk = -yk;
        if (nyk > yInnerMin && nyk < yInnerMax) {
          scatterWeighted(fx, nyk * scaleY + biasY, wR, wG, wB);
        }
      }
    }
  };

  const GLOBAL_X_MIN = -2.02;
  const GLOBAL_X_MAX = 0.50;
  const GLOBAL_Y_MAX = 1.20;
  const isOverview = xMin <= -1.95 && xMax >= 0.48 && yMin <= -1.15 && yMax >= 1.15;

  const sampleUpperHalfPass = (domXMin, domXMax, domYMin, domYMax, colsX, baseOrbitWeight, baseShipWeight) => {
    const domXSpan = domXMax - domXMin;
    const domYSpan = domYMax - domYMin;
    if (!(domXSpan > 0 && domYSpan > 0 && colsX > 0)) return;
    const dx = domXSpan / colsX;
    const dy = domYSpan / samplesY;
    for (let i = startSampleY; i < endSampleY; i += rowStride) {
      const y0 = domYMin + (i + 0.5) * dy;
      if (!enableSubSampling) {
        for (let j = 0; j < colsX; j++) {
          const x0 = domXMin + (j + 0.5) * dx;
          traceAndScatter(x0, y0, baseOrbitWeight, baseShipWeight, true);
        }
        continue;
      }
      let activeCount = 0;
      for (let j = 0; j < colsX; j++) {
        const x0 = domXMin + (j + 0.5) * dx;
        const it = quickBuddhabrotEscapeIter(x0, y0, maxIter);
        if (it >= 15 && it < BUDDHABROT_MAX_ITERATIONS) {
          activeCols[activeCount++] = j;
        }
      }
      if (activeCount === 0) continue;
      const activeRatio = activeCount / colsX;
      const subDiv = activeRatio < 0.14 ? 4 : (activeRatio < 0.34 ? 3 : 2);
      const subCount = subDiv * subDiv;
      const shipW = baseShipWeight / subCount;
      const orbW = baseOrbitWeight / subCount;
      const invSub = 1 / subDiv;
      const cellYMin = domYMin + i * dy;
      for (let a = 0; a < activeCount; a++) {
        const j = activeCols[a];
        const cellXMin = domXMin + j * dx;
        for (let sy = 0; sy < subDiv; sy++) {
          const sy0 = Math.max(1e-7, cellYMin + (sy + 0.5) * invSub * dy);
          for (let sx = 0; sx < subDiv; sx++) {
            const sx0 = cellXMin + (sx + 0.5) * invSub * dx;
            traceAndScatter(sx0, sy0, orbW, shipW, true);
          }
        }
      }
    }
  };

  const baseAreaWeight = ((canvasWidth * canvasHeight) / (samplesX * samplesY)) * rowStride;
  if (isOverview) {
    sampleUpperHalfPass(GLOBAL_X_MIN, GLOBAL_X_MAX, 0, GLOBAL_Y_MAX, samplesX, rowStride, baseAreaWeight);
  } else {
    const padX = xSpan * 0.14;
    const padY = ySpan * 0.16;
    const locXMin = Math.max(GLOBAL_X_MIN, xMin - padX);
    const locXMax = Math.min(GLOBAL_X_MAX, xMax + padX);
    const yAbsMin = (yMin > 0 ? yMin : (yMax < 0 ? -yMax : 0));
    const yAbsMax = Math.max(Math.abs(yMin), Math.abs(yMax));
    const locYMin = Math.max(0, yAbsMin - padY);
    const locYMax = Math.min(GLOBAL_Y_MAX, yAbsMax + padY);
    sampleUpperHalfPass(
      locXMin,
      locXMax,
      locYMin,
      locYMax,
      samplesX,
      rowStride,
      baseAreaWeight * 0.72,
    );
  }

  return histBuffer;
}

function padFilteredBorders(buf, width, height) {
  if (width < 4 || height < 4) return;
  const xMaxValid = width - 3;
  const yMaxValid = height - 3;
  for (let y = 1; y <= yMaxValid; y++) {
    const row = y * width;
    buf[row] = buf[row + 1];
    buf[row + width - 2] = buf[row + xMaxValid];
    buf[row + width - 1] = buf[row + xMaxValid];
  }
  buf.set(buf.subarray(width, width * 2), 0);
  buf.set(buf.subarray(yMaxValid * width, (yMaxValid + 1) * width), (height - 2) * width);
  buf.set(buf.subarray(yMaxValid * width, (yMaxValid + 1) * width), (height - 1) * width);
}

function despikeBoundaryHistogramInPlace(shipHist, width, height, sMax, minRow, maxRow, tmp) {
  if (!(sMax > 0) || minRow > maxRow || width < 5 || height < 5) return;
  const wm2 = width - 2;
  const hm2 = height - 2;
  const yStart = Math.max(1, minRow);
  const yEnd = Math.min(hm2, maxRow);

  tmp.set(shipHist);
  for (let y = yStart; y <= yEnd; y++) {
    const row = y * width;
    for (let x = 1; x <= wm2; x++) {
      const idx = row + x;
      const v = tmp[idx];
      if (v <= 0) continue;
      const l1 = tmp[idx - 1], r1 = tmp[idx + 1];
      const u1 = tmp[idx - width], d1 = tmp[idx + width];
      const ul1 = tmp[idx - width - 1], dr1 = tmp[idx + width + 1];
      const ur1 = tmp[idx - width + 1], dl1 = tmp[idx + width - 1];
      // Find top two neighbors to preserve 1px filaments while despiking isolated hot pixels
      let m1 = 0;
      let m2 = 0;
      const nbs = [l1, r1, u1, d1, ul1, dr1, ur1, dl1];
      for (let k = 0; k < 8; k++) {
        const nv = nbs[k];
        if (nv >= m1) {
          m2 = m1;
          m1 = nv;
        } else if (nv > m2) {
          m2 = nv;
        }
      }
      const ref = m2 * 1.85 + m1 * 0.35 + sMax * 0.025;
      if (v > ref) {
        shipHist[idx] = ref + (v - ref) * 0.2;
      }
    }
  }
}

function atrousBilateralPass(src, dst, width, height, minRow, maxRow, step, invSigmaSq, maxRef) {
  const xMinBound = 1;
  const xMaxBound = Math.max(1, width - 3);
  const yMinBound = 1;
  const yMaxBound = Math.max(1, height - 3);
  const yStart = Math.max(yMinBound, minRow);
  const yEnd = Math.min(yMaxBound, maxRow);
  const sw = step * width;
  const noiseFloor = maxRef * 0.025;

  for (let y = yStart; y <= yEnd; y++) {
    const row = y * width;
    const yn = y - step >= yMinBound ? row - sw : yMinBound * width;
    const yp = y + step <= yMaxBound ? row + sw : yMaxBound * width;
    for (let x = xMinBound; x <= xMaxBound; x++) {
      const idx = row + x;
      const c = src[idx];
      const xn = x - step >= xMinBound ? x - step : xMinBound;
      const xp = x + step <= xMaxBound ? x + step : xMaxBound;

      const v00 = src[yn + xn], v01 = src[yn + x], v02 = src[yn + xp];
      const v10 = src[row + xn],                    v12 = src[row + xp];
      const v20 = src[yp + xn], v21 = src[yp + x], v22 = src[yp + xp];

      if (c + v00 + v01 + v02 + v10 + v12 + v20 + v21 + v22 === 0) {
        dst[idx] = 0;
        continue;
      }

      const localScale = (c + noiseFloor) / invSigmaSq;

      const d01 = v01 - c; const w01 = 2 / (1 + (d01 * d01) / localScale);
      const d10 = v10 - c; const w10 = 2 / (1 + (d10 * d10) / localScale);
      const d12 = v12 - c; const w12 = 2 / (1 + (d12 * d12) / localScale);
      const d21 = v21 - c; const w21 = 2 / (1 + (d21 * d21) / localScale);

      const d00 = v00 - c; const w00 = 1 / (1 + (d00 * d00) / localScale);
      const d02 = v02 - c; const w02 = 1 / (1 + (d02 * d02) / localScale);
      const d20 = v20 - c; const w20 = 1 / (1 + (d20 * d20) / localScale);
      const d22 = v22 - c; const w22 = 1 / (1 + (d22 * d22) / localScale);

      const wSum = 4 + w01 + w10 + w12 + w21 + w00 + w02 + w20 + w22;
      dst[idx] = (
        4 * c +
        w01 * v01 + w10 * v10 + w12 * v12 + w21 * v21 +
        w00 * v00 + w02 * v02 + w20 * v20 + w22 * v22
      ) / wSum;
    }
  }
  padFilteredBorders(dst, width, height);
}

function gaussian5x5Pass(src, dst, width, height, minRow, maxRow, tmp) {
  const xMinBound = 1;
  const xMaxBound = Math.max(1, width - 3);
  const yMinBound = 1;
  const yMaxBound = Math.max(1, height - 3);
  const yStart = Math.max(yMinBound, minRow);
  const yEnd = Math.min(yMaxBound, maxRow);

  for (let y = yStart; y <= yEnd; y++) {
    const row = y * width;
    for (let x = xMinBound; x <= xMaxBound; x++) {
      const xm2 = x - 2 >= xMinBound ? x - 2 : xMinBound;
      const xm1 = x - 1 >= xMinBound ? x - 1 : xMinBound;
      const xp1 = x + 1 <= xMaxBound ? x + 1 : xMaxBound;
      const xp2 = x + 2 <= xMaxBound ? x + 2 : xMaxBound;
      tmp[row + x] = (
        src[row + xm2] +
        4 * src[row + xm1] +
        6 * src[row + x] +
        4 * src[row + xp1] +
        src[row + xp2]
      ) * 0.0625;
    }
  }
  padFilteredBorders(tmp, width, height);

  for (let y = yStart; y <= yEnd; y++) {
    const ym2 = (y - 2 >= yMinBound ? y - 2 : yMinBound) * width;
    const ym1 = (y - 1 >= yMinBound ? y - 1 : yMinBound) * width;
    const y0 = y * width;
    const yp1 = (y + 1 <= yMaxBound ? y + 1 : yMaxBound) * width;
    const yp2 = (y + 2 <= yMaxBound ? y + 2 : yMaxBound) * width;
    for (let x = xMinBound; x <= xMaxBound; x++) {
      dst[y0 + x] = (
        tmp[ym2 + x] +
        4 * tmp[ym1 + x] +
        6 * tmp[y0 + x] +
        4 * tmp[yp1 + x] +
        tmp[yp2 + x]
      ) * 0.0625;
    }
  }
  padFilteredBorders(dst, width, height);
}

function adaptiveSmoothChannelInPlace(hist, width, height, maxRef, minRow, maxRow, tmp, smoothBuf) {
  if (!(maxRef > 0) || minRow > maxRow || width < 3 || height < 3) return;
  if (width < 7 || height < 7) {
    const wm1 = width - 1;
    const hm1 = height - 1;
    const yStart = minRow > 0 ? minRow - 1 : 0;
    const yEnd = maxRow < hm1 ? maxRow + 1 : hm1;
    for (let y = yStart; y <= yEnd; y++) {
      const row = y * width;
      tmp[row] = (3 * hist[row] + hist[row + 1]) * 0.25;
      for (let x = 1; x < wm1; x++) {
        const idx = row + x;
        tmp[idx] = (hist[idx - 1] + 2 * hist[idx] + hist[idx + 1]) * 0.25;
      }
      tmp[row + wm1] = (hist[row + wm1 - 1] + 3 * hist[row + wm1]) * 0.25;
    }
    const knee = maxRef * 0.22;
    const invKnee = 1 / knee;
    for (let y = minRow; y <= maxRow; y++) {
      const ym1 = (y > 0 ? y - 1 : 0) * width;
      const y0 = y * width;
      const yp1 = (y < hm1 ? y + 1 : hm1) * width;
      for (let x = 0; x < width; x++) {
        const idx = y0 + x;
        const sharp = hist[idx];
        if (sharp >= knee) continue;
        const mid = tmp[idx];
        const v1 = tmp[ym1 + x] + tmp[yp1 + x];
        if (mid === 0 && v1 === 0) continue;
        const smooth = (v1 + 2 * mid) * 0.25;
        const m = sharp > smooth ? sharp : smooth;
        if (m >= knee) continue;
        const t = m * invKnee;
        hist[idx] = smooth + (sharp - smooth) * (t * t);
      }
    }
    return;
  }

  padFilteredBorders(hist, width, height);

  const wm2 = width - 2;
  const hm2 = height - 2;
  const yStart = Math.max(2, minRow);
  const yEnd = Math.min(hm2 - 1, maxRow);
  const w2 = width * 2;
  for (let pass = 0; pass < 2; pass++) {
    const src = pass === 0 ? hist : tmp;
    const dst = pass === 0 ? tmp : hist;
    dst.set(src);
    for (let y = yStart; y <= yEnd; y++) {
      const row = y * width;
      for (let x = 2; x < wm2; x++) {
        const idx = row + x;
        const v = src[idx];
        if (v <= 0) continue;
        const l1 = src[idx - 1], r1 = src[idx + 1];
        const u1 = src[idx - width], d1 = src[idx + width];
        const ul1 = src[idx - width - 1], dr1 = src[idx + width + 1];
        const ur1 = src[idx - width + 1], dl1 = src[idx + width - 1];
        const sup1 = Math.max(
          l1 < r1 ? l1 : r1,
          u1 < d1 ? u1 : d1,
          ul1 < dr1 ? ul1 : dr1,
          ur1 < dl1 ? ur1 : dl1,
        );
        const l2 = src[idx - 2], r2 = src[idx + 2];
        const u2 = src[idx - w2], d2 = src[idx + w2];
        const ul2 = src[idx - w2 - 2], dr2 = src[idx + w2 + 2];
        const ur2 = src[idx - w2 + 2], dl2 = src[idx - w2 - 2];
        const sup2 = Math.max(
          l2 < r2 ? l2 : r2,
          u2 < d2 ? u2 : d2,
          ul2 < dr2 ? ul2 : dr2,
          ur2 < dl2 ? ur2 : dl2,
        );
        const nMean = (l1 + r1 + u1 + d1 + ul1 + ur1 + dl1 + dr1) * 0.125;
        const ridge = sup1 < sup2 * 1.2 ? sup1 : sup2 * 1.2;
        const support = ridge > nMean ? ridge : nMean;
        if (v > support * 1.18 + maxRef * 0.002) {
          dst[idx] = support * 1.04 + (v - support * 1.04) * 0.08;
        }
      }
    }
    padFilteredBorders(dst, width, height);
  }

  const invSigmaSq = 1 / (maxRef * 0.085);
  atrousBilateralPass(hist, tmp, width, height, 1, height - 3, 1, invSigmaSq, maxRef);
  atrousBilateralPass(tmp, smoothBuf, width, height, 1, height - 3, 2, invSigmaSq, maxRef);
  atrousBilateralPass(smoothBuf, tmp, width, height, 1, height - 3, 3, invSigmaSq * 1.2, maxRef);
  gaussian5x5Pass(tmp, hist, width, height, 1, height - 3, smoothBuf);
}

function fastPositiveQuantileAndBounds(arr, width, height, q, binsScratch) {
  let maxVal = 0;
  let nonZeroCount = 0;
  let firstIdx = -1;
  let lastIdx = -1;
  const len = arr.length;
  const step = len > 90000 ? 4 : 1;
  for (let i = 0; i < len; i += step) {
    const v = arr[i];
    if (v > 0) {
      if (firstIdx < 0) firstIdx = i;
      lastIdx = i;
      if (v > maxVal) maxVal = v;
    }
  }
  if (firstIdx < 0 || !(maxVal > 0)) {
    return { quantile: 1, floor: 0, minRow: height, maxRow: -1 };
  }
  const dustFloor = maxVal * 0.0015;
  const minRow = Math.max(0, ((firstIdx / width) | 0) - 2);
  const maxRow = Math.min(height - 1, ((lastIdx / width) | 0) + 2);
  binsScratch.fill(0);
  const invMax = 1 / maxVal;
  const maxBin = BUDDHABROT_QUANTILE_BINS - 1;
  for (let i = firstIdx; i <= lastIdx; i += step) {
    const v = arr[i];
    if (v > dustFloor) {
      nonZeroCount++;
      binsScratch[(Math.sqrt(v * invMax) * maxBin) | 0]++;
    }
  }
  if (nonZeroCount === 0) {
    return { quantile: 1, floor: 0, minRow: height, maxRow: -1 };
  }
  const targetFromTop = Math.max(1, Math.ceil(nonZeroCount * (1 - q)));
  let acc = 0;
  let quantile = maxVal;
  for (let b = maxBin; b >= 0; b--) {
    acc += binsScratch[b];
    if (acc >= targetFromTop) {
      const u = (b + 0.5) / BUDDHABROT_QUANTILE_BINS;
      quantile = Math.max(1e-6, u * u * maxVal);
      break;
    }
  }
  const coverage = (nonZeroCount * step) / len;
  let floor = 0;
  if (coverage > 0.65 && len >= 256) {
    const targetLow = Math.max(1, Math.floor(nonZeroCount * 0.18));
    let accLow = 0;
    for (let b = 0; b <= maxBin; b++) {
      accLow += binsScratch[b];
      if (accLow >= targetLow) {
        const uLow = (b + 0.5) / BUDDHABROT_QUANTILE_BINS;
        const strength = Math.min(1, (coverage - 0.65) / 0.28);
        floor = uLow * uLow * maxVal * 0.72 * strength;
        break;
      }
    }
  }
  return { quantile: Math.max(quantile, maxVal * 0.62), floor, minRow, maxRow };
}

/**
 * Convert a 4-plane Buddhabrot histogram buffer into an RGBA Uint8ClampedArray.
 */
export function finalizeBuddhabrotPixels(histBuffer, canvasWidth, canvasHeight) {
  const pixelCount = canvasWidth * canvasHeight;
  const rgba = new Uint8ClampedArray(pixelCount * 4);
  if (!(pixelCount > 0) || histBuffer.length < pixelCount * 4) return rgba;
  const rgba32 = new Uint32Array(rgba.buffer);
  const { toneLut, sqrtLut } = getBuddhabrotLuts();
  const { binsScratch, smoothScratch, smoothBuf } = getBuddhabrotScratch(pixelCount);

  const rHist = histBuffer.subarray(0, pixelCount);
  const gHist = histBuffer.subarray(pixelCount, pixelCount * 2);
  const bHist = histBuffer.subarray(pixelCount * 2, pixelCount * 3);
  const shipHist = histBuffer.subarray(pixelCount * 3, pixelCount * 4);

  const rStats = fastPositiveQuantileAndBounds(rHist, canvasWidth, canvasHeight, 0.9985, binsScratch);
  const gStats = fastPositiveQuantileAndBounds(gHist, canvasWidth, canvasHeight, 0.9985, binsScratch);
  const bStats = fastPositiveQuantileAndBounds(bHist, canvasWidth, canvasHeight, 0.9985, binsScratch);
  const sStats = fastPositiveQuantileAndBounds(shipHist, canvasWidth, canvasHeight, 0.995, binsScratch);
  const rMax = Math.max(2, rStats.quantile);
  const gMax = Math.max(2, gStats.quantile);
  const bMax = Math.max(1.5, bStats.quantile);
  const sMax = Math.max(12, sStats.quantile * 1.15);
  const rFloor = Math.min(rMax * 0.35, rStats.floor);
  const gFloor = Math.min(gMax * 0.35, gStats.floor);
  const bFloor = Math.min(bMax * 0.35, bStats.floor);

  adaptiveSmoothChannelInPlace(rHist, canvasWidth, canvasHeight, rMax, rStats.minRow, rStats.maxRow, smoothScratch, smoothBuf);
  adaptiveSmoothChannelInPlace(gHist, canvasWidth, canvasHeight, gMax, gStats.minRow, gStats.maxRow, smoothScratch, smoothBuf);
  adaptiveSmoothChannelInPlace(bHist, canvasWidth, canvasHeight, bMax, bStats.minRow, bStats.maxRow, smoothScratch, smoothBuf);
  despikeBoundaryHistogramInPlace(shipHist, canvasWidth, canvasHeight, sMax, sStats.minRow, sStats.maxRow, smoothScratch);

  const scaleR = BUDDHABROT_TONE_LUT_SIZE / Math.max(1, (rMax - rFloor) * 1.08);
  const scaleG = BUDDHABROT_TONE_LUT_SIZE / Math.max(1, (gMax - gFloor) * 1.08);
  const scaleB = BUDDHABROT_TONE_LUT_SIZE / Math.max(1, (bMax - bFloor) * 1.08);
  const scaleS = BUDDHABROT_TONE_LUT_SIZE / sMax;
  const bg32 = 0xff080301;
  rgba32.fill(bg32);

  const minRow = Math.min(rStats.minRow, gStats.minRow, bStats.minRow, sStats.minRow);
  const maxRow = Math.max(rStats.maxRow, gStats.maxRow, bStats.maxRow, sStats.maxRow);
  if (minRow > maxRow) return rgba;
  const startPixel = Math.max(0, (minRow > 0 ? minRow - 1 : 0) * canvasWidth);
  const endPixel = Math.min(pixelCount, (maxRow + 2) * canvasWidth);

  for (let i = startPixel; i < endPixel; i++) {
    const rv = rHist[i] > rFloor ? rHist[i] - rFloor : 0;
    const gv = gHist[i] > gFloor ? gHist[i] - gFloor : 0;
    const bv = bHist[i] > bFloor ? bHist[i] - bFloor : 0;
    const sv = shipHist[i];
    if (rv + gv + bv + sv <= 0) continue;
    const sIdx = (sv * scaleS) | 0;
    const rIdx = (rv * scaleR) | 0;
    const gIdx = (gv * scaleG) | 0;
    const bIdx = (bv * scaleB) | 0;
    const sn = sqrtLut[sIdx > BUDDHABROT_TONE_LUT_SIZE ? BUDDHABROT_TONE_LUT_SIZE : sIdx];
    const tr = toneLut[rIdx > BUDDHABROT_TONE_LUT_SIZE ? BUDDHABROT_TONE_LUT_SIZE : rIdx];
    const tg = toneLut[gIdx > BUDDHABROT_TONE_LUT_SIZE ? BUDDHABROT_TONE_LUT_SIZE : gIdx];
    const tb = toneLut[bIdx > BUDDHABROT_TONE_LUT_SIZE ? BUDDHABROT_TONE_LUT_SIZE : bIdx];

    // Wikipedia Nebulabrot color matrix:
    // tr (fast / outer veil): deep atmospheric amethyst-purple (B > R > G)
    // tg (mid / caustics): vibrant electric sapphire & cobalt (B >> G > R)
    // tb (slow / inner core): radiant ice-cyan & celestial azure (B >= G > R)
    // sn (filaments): incandescent electric cyan-white with subtle warm core
    const causticEnergy = (tb * 1.25 + tg * 0.85 + tr * 0.45);
    const coreBloom = Math.pow(Math.max(0, causticEnergy - 0.65), 1.6) * 0.68 + sn * sn * 0.75;

    const rawR = tr * 0.48 + tg * 0.16 + tb * 0.72 + sn * 0.82 + coreBloom * 0.88;
    const rawG = tr * 0.18 + tg * 0.52 + tb * 0.96 + sn * 0.94 + coreBloom * 0.92;
    const rawB = tr * 0.88 + tg * 1.18 + tb * 1.36 + sn * 1.22 + coreBloom * 0.98;

    // Exponential filmic tone compression preserving ethereal glow and luminous white highlights
    const peak = Math.max(rawR, rawG, rawB, 1e-6);
    const mappedPeak = 1 - Math.exp(-peak * 1.38);
    const scaleTone = mappedPeak / peak;
    const r = Math.min(1, rawR * scaleTone + coreBloom * 0.16);
    const g = Math.min(1, rawG * scaleTone + coreBloom * 0.16);
    const b = Math.min(1, rawB * scaleTone + coreBloom * 0.12);

    const maxC = r > g ? (r > b ? r : b) : (g > b ? g : b);
    const a = maxC * 1.45 > 1 ? 1 : maxC * 1.45;
    const oma = 1 - a;
    const prRaw = (1 * oma + (r > 1 ? 255 : r * 255) + 0.5) | 0;
    const pgRaw = (3 * oma + (g > 1 ? 255 : g * 255) + 0.5) | 0;
    const pbRaw = (8 * oma + (b > 1 ? 255 : b * 255) + 0.5) | 0;
    const pr = prRaw > 255 ? 255 : prRaw;
    const pg = pgRaw > 255 ? 255 : pgRaw;
    const pb = pbRaw > 255 ? 255 : pbRaw;
    rgba32[i] = pr | (pg << 8) | (pb << 16) | 0xff000000;
  }

  return rgba;
}

export function renderBuddhabrotRgba(params) {
  const histBuffer = accumulateBuddhabrotHistogram(params);
  return finalizeBuddhabrotPixels(histBuffer, params.canvasWidth, params.canvasHeight);
}

const iterResult = { iterations: 0, mu: 0, shade: 1 };

// Reusable object for colorFunc to avoid allocation in hot loop
const colorInput = { numIterations: 0, mu: 0, shade: 1, x0: NaN, y0: NaN };
const clampByte = value => value <= 0 ? 0 : value >= 255 ? 255 : value | 0;
const packRgba32 = color =>
  clampByte(color[0]) | (clampByte(color[1]) << 8) | (clampByte(color[2]) << 16) | (clampByte(color[3]) << 24);

/**
 * Given a canvas element, draw a fractal using the escape time method
 * Highly optimized:
 * - Precomputed scale factors
 * - Reusable objects to avoid allocations
 * - Inlined pixel writes
 */
export function drawMandelbrotFractal(canvasEl, xRange, yRange, colorFunc, useMu = true, paletteParams = null) {
  const canvasWidth = canvasEl.width;
  const canvasHeight = canvasEl.height;
  if (!(canvasWidth > 0 && canvasHeight > 0)) return;
  const context = canvasEl.getContext('2d');
  if (!context) return;
  if (colorFunc === buddhabrotColor
    || isBuddhabrotMode(colorFunc?.colorParams?.mode)
    || isBuddhabrotMode(paletteParams?.mode)) {
    const pixels = renderBuddhabrotRgba({
      canvasWidth,
      canvasHeight,
      xRange,
      yRange,
    });
    if (typeof ImageData === 'function') {
      context.putImageData(new ImageData(pixels, canvasWidth, canvasHeight), 0, 0);
    } else {
      const img = context.createImageData(canvasWidth, canvasHeight);
      img.data.set(pixels);
      context.putImageData(img, 0, 0);
    }
    return;
  }
  const canvasImageData = context.createImageData(canvasWidth, canvasHeight);
  const image = canvasImageData.data;
  const image32 = new Uint32Array(image.buffer);
  
  // Precompute scale factors to avoid division in inner loop
  const xScale = (xRange[1] - xRange[0]) / canvasWidth;
  const yScale = (yRange[1] - yRange[0]) / canvasHeight;
  const xBase = xRange[0];
  const yBase = yRange[0];
  
  const result = iterResult;
  const reusablePaletteColor = paletteParams ? [0, 0, 0, 0] : null;
  const patternMode = useMu && isPatternMode(paletteParams?.mode) ? paletteParams.mode : null;
  // A custom callback may depend on call order, so only the built-in plain
  // palette path can reuse a complete conjugate row without changing it.
  const paletteDescriptors = paletteParams && Object.getPrototypeOf(paletteParams) === Object.prototype
    ? Object.getOwnPropertyDescriptors(paletteParams) : null;
  const stablePalette = !patternMode && paletteDescriptors && Object.values(paletteDescriptors).every(descriptor =>
    Object.prototype.hasOwnProperty.call(descriptor, 'value')) &&
    canCertifyCpuPalette(paletteParams);
  const conjugateRows = stablePalette ? fullConjugateRowMap({ canvasWidth, canvasHeight,
    xRange, yRange, colorParams: paletteParams }) : null;
  
  for (let i = 0; i < canvasHeight; i++) {
    const y0 = yBase + (i + 0.5) * yScale;
    const rowStart32 = i * canvasWidth;
    const partner = conjugateRows?.partners[i] ?? -1;
    if (partner >= 0) {
      const partnerStart32 = partner * canvasWidth;
      image32.copyWithin(rowStart32, partnerStart32, partnerStart32 + canvasWidth);
      continue;
    }
    
    for (let j = 0; j < canvasWidth; j++) {
      const x0 = xBase + (j + 0.5) * xScale;
      if (patternMode) iterateUntilEscapePatternMode(x0, y0, result, patternMode, undefined, useMu);
      else iterateUntilEscape(x0, y0, result, undefined, useMu);
      
      if (result.escaped) {
        let color;
        if (paletteParams) {
          color = samplePalette(paletteParams.useMu === false ? result.iterations : result.mu,
            paletteParams, reusablePaletteColor, patternMode ? result.shade : 1);
        } else {
          colorInput.numIterations = result.iterations;
          colorInput.mu = result.mu;
          colorInput.shade = result.shade;
          colorInput.x0 = x0;
          colorInput.y0 = y0;
          color = colorFunc(colorInput);
        }
        image32[rowStart32 + j] = packRgba32(color);
      }
    }
  }
  colorInput.x0 = NaN;
  colorInput.y0 = NaN;
  context.putImageData(canvasImageData, 0, 0);
}

/**
 * Check if a range change is a pure pan (same scale, different offset)
 */
function isPanOnly(oldXRange, oldYRange, newXRange, newYRange) {
  const oldXSpan = oldXRange[1] - oldXRange[0];
  const oldYSpan = oldYRange[1] - oldYRange[0];
  const newXSpan = newXRange[1] - newXRange[0];
  const newYSpan = newYRange[1] - newYRange[0];
  
  // Check if spans are equal (within floating point tolerance)
  const tolerance = 1e-10;
  return Math.abs(oldXSpan - newXSpan) < tolerance && 
         Math.abs(oldYSpan - newYSpan) < tolerance;
}

function renderPanRegion(x, y, width, height, xBase, yBase, xScale, yScale, colorFunc, useMu) {
  const pixels = new Uint8ClampedArray(width * height * 4);
  const pixels32 = new Uint32Array(pixels.buffer);
  for (let row = 0; row < height; row++) {
    const y0 = yBase + (y + row + 0.5) * yScale;
    for (let col = 0; col < width; col++) {
      const x0 = xBase + (x + col + 0.5) * xScale;
      iterateUntilEscape(x0, y0, iterResult, undefined, useMu);
      if (!iterResult.escaped) continue;
      colorInput.numIterations = iterResult.iterations;
      colorInput.mu = iterResult.mu;
      colorInput.shade = iterResult.shade;
      colorInput.x0 = x0;
      colorInput.y0 = y0;
      pixels32[row * width + col] = packRgba32(colorFunc(colorInput));
    }
  }
  colorInput.x0 = NaN;
  colorInput.y0 = NaN;
  return { x, y, width, height, pixels };
}

/**
 * Apply a pan to existing canvas content and render only new edges
 * Returns true if pan was applied, false if full re-render needed
 * 
 * Work is limited to exposed strips. The canvas backend moves retained pixels
 * so no full-canvas readback or JS pixel copy is needed.
 */
export function applyPanAndRenderEdges(canvasEl, oldXRange, oldYRange, newXRange, newYRange, colorFunc, useMu = true) {
  if (!isPanOnly(oldXRange, oldYRange, newXRange, newYRange)) return false;
  const canvasWidth = canvasEl.width;
  const canvasHeight = canvasEl.height;
  const context = canvasEl.getContext('2d');
  const xScale = (newXRange[1] - newXRange[0]) / canvasWidth;
  const yScale = (newYRange[1] - newYRange[0]) / canvasHeight;
  const pixelOffsetX = Math.round((newXRange[0] - oldXRange[0]) / xScale);
  const pixelOffsetY = Math.round((newYRange[0] - oldYRange[0]) / yScale);
  if (!Number.isFinite(pixelOffsetX) || !Number.isFinite(pixelOffsetY) ||
      Math.abs(pixelOffsetX) > canvasWidth * 0.8 ||
      Math.abs(pixelOffsetY) > canvasHeight * 0.8) return false;
  if (pixelOffsetX === 0 && pixelOffsetY === 0) return true;
  const xBase = newXRange[0];
  const yBase = newYRange[0];
  const regions = [];
  if (pixelOffsetX !== 0) {
    const stripWidth = Math.abs(pixelOffsetX);
    const stripX = pixelOffsetX > 0 ? canvasWidth - stripWidth : 0;
    regions.push(renderPanRegion(stripX, 0, stripWidth, canvasHeight,
      xBase, yBase, xScale, yScale, colorFunc, useMu));
  }
  if (pixelOffsetY !== 0) {
    const stripHeight = Math.abs(pixelOffsetY);
    const stripY = pixelOffsetY > 0 ? canvasHeight - stripHeight : 0;
    const stripX = Math.max(0, -pixelOffsetX);
    regions.push(renderPanRegion(stripX, stripY, canvasWidth - Math.abs(pixelOffsetX), stripHeight,
      xBase, yBase, xScale, yScale, colorFunc, useMu));
  }
  // Complete numerical work before changing visible canvas pixels.
  presentPanTiles(canvasEl, context, { pixelOffsetX, pixelOffsetY, regions });
  return true;
}
